import { useState } from 'react';
import { ReviewContent } from './ReviewContent';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  ActionIcon,
  Badge,
  Image,
  Loader,
  Menu,
  Tooltip,
} from '@mantine/core';
import {
  IconThumbUp,
  IconThumbUpFilled,
  IconThumbDown,
  IconThumbDownFilled,
  IconMessageCircle,
  IconSend2,
  IconDotsVertical,
  IconFlag,
  IconTrash,
  IconEdit,
  IconUser,
  IconBookmark,
  IconBookmarkFilled,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { UserRole } from '../../types';
import type { Review } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { reviewsService } from '../../services/reviews';
import { StarRating } from './StarRating';
import { ReportModal } from '../common/ReportModal';
import { ImageLightbox } from './ImageLightbox';
import { LoginPromptModal } from '../common/LoginPromptModal';
import { getBadgeMeta } from '../../utils/badges';
import { formatRelativeDate, formatFullDate } from '../../utils/dateUtils';

const TAG_SENTIMENT_COLOR: Record<string, string> = {
  'overpriced': 'red',    'feels-cheap': 'red',    'disappointing': 'red',
  'fair-price': 'yellow', 'does-the-job': 'yellow', 'as-expected': 'yellow',
  'great-value': 'teal',  'premium-quality': 'teal', 'exceeded-expectations': 'teal',
};

interface ReviewCardProps {
  review: Review;
  showProduct?: boolean;
  variant?: 'list' | 'grid';
  onHelpfulChange?: (reviewId: string, helpfulCount: number) => void;
  onDelete?: (reviewId: string) => void;
  onUnsave?: (reviewId: string) => void;
}


export function ReviewCard({ review, showProduct = true, variant = 'list', onHelpfulChange, onDelete, onUnsave }: ReviewCardProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isHelpful, setIsHelpful] = useState(review.currentUserVotedHelpful === true);
  const [isNotHelpful, setIsNotHelpful] = useState(review.currentUserVotedHelpful === false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpfulCount);
  const [notHelpfulCount, setNotHelpfulCount] = useState(review.notHelpfulCount);
  const [commentCount] = useState(review.commentCount);
  const [isSaved, setIsSaved] = useState(review.isSaved ?? false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [loginPromptAction, setLoginPromptAction] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const isOwner = user?.id === review.user.id;
  const isAdmin = user?.role === UserRole.Admin;

  const handleCardClick = (e: React.MouseEvent) => {
    // Only navigate if clicking on the card itself, not on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('a, button, [role="button"], [data-interactive]')) {
      return;
    }
    navigate(`/reviews/${review.id}`);
  };

  const voteMutation = useMutation({
    mutationFn: (vote: boolean) => reviewsService.voteHelpful(review.id, vote),
    onSuccess: (result) => {
      setIsHelpful(result.currentUserVotedHelpful === true);
      setIsNotHelpful(result.currentUserVotedHelpful === false);
      setHelpfulCount(result.helpfulCount);
      setNotHelpfulCount(result.notHelpfulCount);
      onHelpfulChange?.(review.id, result.helpfulCount);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update vote', color: 'red' });
    },
  });

  const handleVote = (vote: boolean) => {
    if (!isAuthenticated) {
      setLoginPromptAction('vote on reviews');
      return;
    }
    if (voteMutation.isPending) return;
    voteMutation.mutate(vote);
  };

  const getShareUrl = () => `${window.location.origin}/reviews/${review.id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      notifications.show({
        title: 'Link copied',
        message: 'Review link copied to clipboard',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to copy link',
        color: 'red',
      });
    }
  };

  const saveMutation = useMutation({
    mutationFn: (currentlySaved: boolean) =>
      currentlySaved ? reviewsService.unsave(review.id) : reviewsService.save(review.id),
    onSuccess: (_, currentlySaved) => {
      setIsSaved(!currentlySaved);
      if (currentlySaved) onUnsave?.(review.id);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update saved status', color: 'red' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => reviewsService.delete(review.id),
    onSuccess: () => {
      notifications.show({ title: 'Deleted', message: 'Review has been deleted', color: 'green' });
      onDelete?.(review.id);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to delete review', color: 'red' });
    },
  });

  const handleSave = () => {
    if (!isAuthenticated) {
      setLoginPromptAction('save reviews');
      return;
    }
    if (saveMutation.isPending) return;
    saveMutation.mutate(isSaved);
  };

  const handleDelete = () => {
    if (!confirm('Are you sure you want to delete this review?')) return;
    deleteMutation.mutate();
  };

  const isGrid = variant === 'grid';

  return (
    <>
    <Box
      onClick={handleCardClick}
      style={isGrid ? {
        padding: '0.75rem',
        borderBottom: '1px solid var(--mantine-color-default-border)',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
      } : {
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--mantine-color-default-border)',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <Group align="flex-start" gap="xs" wrap="nowrap">
        {/* Avatar */}
        <Link
          to={`/users/${review.user.username}`}
          onClick={(e) => e.stopPropagation()}
          data-interactive
        >
          <Avatar
            src={review.user.profileImageUrl}
            alt={review.user.username}
            radius="xl"
            size="sm"
          >
            <IconUser size={16} />
          </Avatar>
        </Link>

        {/* Content */}
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          {/* Header row */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
              <Link
                to={`/users/${review.user.username}`}
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: 'none', color: 'inherit' }}
                data-interactive
              >
                <Text size="sm" fw={700} truncate>
                  {review.user.username}
                </Text>
              </Link>
              {review.user.activeBadge && (() => {
                const meta = getBadgeMeta(review.user.activeBadge.key);
                return (
                  <Tooltip label={`${meta.label} · ${meta.description}`} withArrow>
                    <Badge size="xs" color={meta.color} variant="light" style={{ cursor: 'default' }}>
                      {meta.emoji}
                    </Badge>
                  </Tooltip>
                );
              })()}
              <Text size="sm" c="dimmed">·</Text>
              <Tooltip label={formatFullDate(review.createdAt)} withArrow>
                <Text size="sm" c="dimmed" style={{ flexShrink: 0, cursor: 'default' }}>
                  {formatRelativeDate(review.createdAt)}
                </Text>
              </Tooltip>
            </Group>

            <Menu shadow="md" position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                  data-interactive
                >
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconSend2 size={16} />} onClick={handleCopyLink}>
                  Copy link
                </Menu.Item>
                {isOwner && (
                  <>
                    <Menu.Item
                      leftSection={<IconEdit size={16} />}
                      component={Link}
                      to={`/reviews/${review.id}/edit`}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconTrash size={16} />}
                      color="red"
                      onClick={handleDelete}
                    >
                      Delete
                    </Menu.Item>
                  </>
                )}
                {!isOwner && (
                  <Menu.Item
                    leftSection={<IconFlag size={16} />}
                    color="red"
                    onClick={() => setReportModalOpen(true)}
                  >
                    Report
                  </Menu.Item>
                )}
                {isAdmin && !isOwner && (
                  <>
                    <Menu.Divider />
                    <Menu.Label>Admin</Menu.Label>
                    <Menu.Item
                      leftSection={<IconTrash size={16} />}
                      color="red"
                      onClick={handleDelete}
                    >
                      Delete review
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Product name (if showing) */}
          {showProduct && review.product && (
            <Link
              to={`/products/${review.productId}`}
              onClick={(e) => e.stopPropagation()}
              style={{ textDecoration: 'none', alignSelf: 'flex-start' }}
              data-interactive
            >
              <Text size="sm" fw={700} c="brand.6" lineClamp={1}>
                {review.product.name}
              </Text>
              {review.product.brand && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {review.product.brand}
                </Text>
              )}
            </Link>
          )}

          {/* Rating */}
          <Group gap="xs">
            <StarRating rating={review.rating} size="xs" />
            <Text size="xs" c="dimmed">
              {review.rating}/5
            </Text>
          </Group>

          {/* Content */}
          <ReviewContent content={review.content} size="sm" lineClamp={3} />

          {/* Tags */}
          {review.tags && review.tags.length > 0 && (
            <Group gap={4} mt={4}>
              {review.tags.map((tag) => (
                <Badge key={tag.id} size="xs" variant="light" radius="xl" color={TAG_SENTIMENT_COLOR[tag.slug] ?? 'gray'}>
                  {tag.emoji} {tag.label}
                </Badge>
              ))}
            </Group>
          )}

          {/* Purchase info */}
          {(review.purchaseSource || review.purchasePrice) && (
            <Text size="xs" c="dimmed">
              {review.purchaseSource && `Bought at ${review.purchaseSource}`}
              {review.purchaseSource && review.purchasePrice && ' · '}
              {review.purchasePrice && `Paid ${review.purchaseCurrency ?? 'USD'} ${review.purchasePrice.toFixed(2)}`}
            </Text>
          )}

          {/* Promotional disclosure */}
          {review.isPromotional && (
            <Badge size="xs" variant="light" color="orange" radius="sm">
              Received free or discounted for review
            </Badge>
          )}

          {/* Images - show review images, or fall back to product image */}
          {review.imageUrls.length > 0 ? (
            review.imageUrls.length === 1 ? (
              <Box
                mt="xs"
                h={isGrid ? 160 : 250}
                data-interactive
                onClick={() => openLightbox(0)}
                style={{
                  borderRadius: 'var(--mantine-radius-md)',
                  border: '1px solid var(--mantine-color-default-border)',
                  backgroundColor: 'var(--mantine-color-gray-1)',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'zoom-in',
                }}
              >
                <Image
                  src={review.imageUrls[0]}
                  alt="Review image"
                  h={isGrid ? 160 : 250}
                  w="100%"
                  fit="contain"
                />
              </Box>
            ) : (
              <Group gap="xs" mt="xs">
                {review.imageUrls.slice(0, 4).map((url, index) => (
                  <Image
                    key={index}
                    src={url}
                    alt={`Review image ${index + 1}`}
                    w={80}
                    h={80}
                    radius="md"
                    fit="cover"
                    data-interactive
                    onClick={() => openLightbox(index)}
                    style={{
                      border: '1px solid var(--mantine-color-default-border)',
                      cursor: 'zoom-in',
                    }}
                  />
                ))}
                {review.imageUrls.length > 4 && (
                  <Badge variant="light" size="sm">
                    +{review.imageUrls.length - 4}
                  </Badge>
                )}
              </Group>
            )
          ) : review.product?.imageUrl ? (
            <Box
              mt="xs"
              h={isGrid ? 160 : 250}
              style={{
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--mantine-color-default-border)',
                backgroundColor: 'var(--mantine-color-gray-1)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={review.product.imageUrl}
                alt={review.product.name}
                h={isGrid ? 160 : 250}
                w="100%"
                fit="contain"
              />
            </Box>
          ) : null}

          {/* Action buttons */}
          <Group grow mt={2} style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }} pt={2}>
            {/* Helpful? 👍 👎 */}
            <Group gap={6} justify="center" py={4} data-interactive data-testid="helpful-vote">
              <Group
                gap={3}
                style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); handleVote(true); }}
              >
                {voteMutation.isPending ? (
                  <Loader size={16} />
                ) : isHelpful ? (
                  <IconThumbUpFilled size={18} color="var(--mantine-color-blue-filled)" />
                ) : (
                  <IconThumbUp size={18} color="var(--mantine-color-dimmed)" />
                )}
                <Text size="xs" c={isHelpful ? 'blue' : 'dimmed'}>{helpfulCount}</Text>
              </Group>
              <Group
                gap={3}
                style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); handleVote(false); }}
              >
                {isNotHelpful ? (
                  <IconThumbDownFilled size={18} color="var(--mantine-color-dimmed)" />
                ) : (
                  <IconThumbDown size={18} color="var(--mantine-color-dimmed)" />
                )}
                <Text size="xs" c="dimmed">{notHelpfulCount}</Text>
              </Group>
            </Group>

            {/* Comment */}
            <Group
              gap={4}
              justify="center"
              py={4}
              style={{ cursor: 'pointer', borderRadius: 'var(--mantine-radius-sm)', transition: 'background-color 150ms ease' }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/reviews/${review.id}#comments`);
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-light)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-interactive
            >
              <IconMessageCircle size={18} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">{commentCount > 0 ? commentCount : ''}</Text>
            </Group>

            {/* Share */}
            <Menu shadow="md" position="top" withinPortal>
              <Menu.Target>
                <Group
                  gap={4}
                  justify="center"
                  py={4}
                  style={{ cursor: 'pointer', borderRadius: 'var(--mantine-radius-sm)', transition: 'background-color 150ms ease' }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-teal-light)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-interactive
                >
                  <IconSend2 size={18} color="var(--mantine-color-dimmed)" />
                </Group>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconSend2 size={18} />} onClick={handleCopyLink}>
                  Copy link
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {/* Save */}
            <Group
              gap={4}
              justify="center"
              py={4}
              data-testid="save-button"
              style={{ cursor: 'pointer', borderRadius: 'var(--mantine-radius-sm)', transition: 'background-color 150ms ease' }}
              onClick={(e) => { e.stopPropagation(); handleSave(); }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-yellow-light)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-interactive
            >
              {isSaved
                ? <IconBookmarkFilled size={18} color="var(--mantine-color-yellow-filled)" />
                : <IconBookmark size={18} color="var(--mantine-color-dimmed)" />}
            </Group>
          </Group>
        </Stack>
      </Group>
    </Box>

    <LoginPromptModal
      opened={loginPromptAction !== null}
      onClose={() => setLoginPromptAction(null)}
      action={loginPromptAction ?? undefined}
    />

    <ReportModal
      opened={reportModalOpen}
      onClose={() => setReportModalOpen(false)}
      targetId={review.id}
      targetType="Review"
      targetUserId={review.user.id}
      targetName="this review"
    />


    <ImageLightbox
      images={review.imageUrls}
      initialIndex={lightboxIndex}
      opened={lightboxOpen}
      onClose={() => setLightboxOpen(false)}
    />
    </>
  );
}
